import React from 'react';
import Image from 'components/image';
import Icon from 'components/icon';
import PropTypes from 'prop-types';
import storyTeaserSizes from './story-teaser-sizes';
import storyTeaserOrientations from './story-teaser-orientations';
import cn from 'classnames';
import { imageSourceBreakpoints } from 'js/breakpoints';

const getImageSources = teaserSize => {
  switch (teaserSize) {
    case storyTeaserSizes.small:
      return [
        { width: 165, height: 198, media: imageSourceBreakpoints.xs },
        { width: 158, height: 140, media: imageSourceBreakpoints.s },
        { width: 204, height: 181, media: imageSourceBreakpoints.m },
        { width: 258, height: 228, media: imageSourceBreakpoints.l },
      ];

    case storyTeaserSizes.medium:
      return [
        { width: 382, height: 268, media: imageSourceBreakpoints.xs },
        { width: 306, height: 323, media: imageSourceBreakpoints.s },
        { width: 384, height: 405, media: imageSourceBreakpoints.m },
        { width: 506, height: 534, media: imageSourceBreakpoints.l },
      ];

    case storyTeaserSizes.large:
      return [
        { width: 430, height: 374, media: imageSourceBreakpoints.xs },
        { width: 720, height: 381, media: imageSourceBreakpoints.s },
        { width: 904, height: 478, media: imageSourceBreakpoints.m },
        { width: 888, height: 470, media: imageSourceBreakpoints.l },
      ];

    default:
      return [];
  }
};

const StoryTeaser = ({
  url,
  heading,
  image,
  backgroundColor,
  categoryLabel,
  className,
  size = storyTeaserSizes.large,
  orientation = storyTeaserOrientations.vertical,
  headingTagLevel = 2,
  preloadImage,
}) => {
  const isHorizontalOrientation =
    orientation === storyTeaserOrientations.horizontal;
  const HeadingTag = `h${headingTagLevel}`;
  return (
    <a
      className={cn(`story-teaser story-teaser_size_${size}`, {
        [className]: className,
        'story-teaser_horizontal': isHorizontalOrientation,
      })}
      href={url}
    >
      <div className="story-teaser__container">
        <div
          className={cn('story-teaser__image-holder', {
            'story-teaser__image-holder_no-image': !image,
          })}
        >
          {image && (
            <Image
              {...image}
              sources={getImageSources(size)}
              {...(!preloadImage && { loading: 'lazy' })}
              {...(preloadImage && { fetchPriority: 'high' })}
              fluid={isHorizontalOrientation}
              className={`story-teaser__image image_size_story-teaser-${size}`}
            />
          )}
        </div>
        <div
          className="story-teaser__content"
          style={{ backgroundColor: backgroundColor }}
        >
          <div className="story-teaser__content-inner">
            {categoryLabel && (
              <span className="story-teaser__category">{categoryLabel}</span>
            )}
            {heading && (
              <HeadingTag className="story-teaser__heading">
                <span className="story-teaser__heading-text">{heading}</span>
              </HeadingTag>
            )}
            <Icon name="arrow-right" className="story-teaser__icon" />
          </div>
        </div>
      </div>
    </a>
  );
};

StoryTeaser.propTypes = {
  id: PropTypes.string,
  url: PropTypes.string,
  heading: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  backgroundColor: PropTypes.string,
  categoryLabel: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(Object.values(storyTeaserSizes)),
  orientation: PropTypes.oneOf(Object.values(storyTeaserOrientations)),
  headingTagLevel: PropTypes.oneOf([2, 3]),
  preloadImage: PropTypes.bool,
};

StoryTeaser.viewModelMeta = {
  size: 'ignore',
  className: 'ignore',
  orientation: 'ignore',
  headingTagLevel: 'ignore',
  preloadImage: 'ignore',
};

export default StoryTeaser;
